import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X, GraduationCap } from "lucide-react";
import { EASE } from "./bits";

const SESSION_KEY = "tnn_mc_seen";
const REGISTERED_KEY = "tnn_mc_registered";

export default function MasterclassModal() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    if (pathname === "/masterclass" || pathname === "/book" || pathname === "/feedback" || pathname.startsWith("/admin")) return;
    if (sessionStorage.getItem(SESSION_KEY) || localStorage.getItem(REGISTERED_KEY)) return;
    const t = setTimeout(() => setOpen(true), 4000);
    return () => clearTimeout(t);
  }, [pathname]);

  const close = () => {
    sessionStorage.setItem(SESSION_KEY, "1");
    setOpen(false);
  };

  const go = (registering) => {
    if (registering) localStorage.setItem(REGISTERED_KEY, "intent");
    sessionStorage.setItem(SESSION_KEY, "1");
    setOpen(false);
    navigate("/masterclass");
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="masterclass-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[90] bg-tnn-deep/50 backdrop-blur-sm flex items-center justify-center p-5"
          onClick={close}
        >
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-labelledby="masterclass-modal-title"
            initial={{ opacity: 0, scale: 0.95, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.97, y: 10 }}
            transition={{ duration: 0.35, ease: EASE }}
            className="relative w-full max-w-md bg-white rounded-3xl border border-tnn-ink/8 shadow-2xl p-8 sm:p-9"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={close}
              data-testid="masterclass-modal-close"
              aria-label="Close"
              className="absolute top-4 right-4 w-9 h-9 rounded-full bg-tnn-warm text-tnn-muted hover:text-tnn-purple flex items-center justify-center transition-colors duration-200"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="flex items-center gap-2.5">
              <span className="w-8 h-8 rounded-full bg-tnn-soft flex items-center justify-center">
                <GraduationCap className="w-4 h-4 text-tnn-purple" />
              </span>
              <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-tnn-purple">TNN Africa Masterclass</p>
            </div>
            <h2
              id="masterclass-modal-title"
              className="mt-4 font-display text-2xl sm:text-[1.7rem] font-bold tracking-tight text-tnn-ink leading-[1.15]"
            >
              Thinking about joining the masterclass?
            </h2>
            <p className="mt-3 text-sm text-tnn-muted leading-relaxed">
              <span className="font-semibold text-tnn-ink">Positioning for Global Leadership</span> is a live career
              growth masterclass for dietitians, nutritionists and nutrition students who want to build visibility,
              influence and access to global career opportunities.
            </p>
            <p className="mt-3 text-xs font-semibold text-tnn-purple">
              December 12, 2026 · 10:00 AM – 1:00 PM WAT · Live Online
            </p>
            <div className="mt-6 flex flex-col gap-2.5">
              <button
                onClick={() => go(true)}
                data-testid="masterclass-modal-yes"
                className="w-full bg-tnn-purple text-white font-bold text-sm tracking-wide px-6 py-3.5 rounded-full hover:bg-tnn-deep transition-colors duration-200"
              >
                YES, I WANT TO REGISTER
              </button>
              <button
                onClick={() => go(false)}
                data-testid="masterclass-modal-checking"
                className="w-full bg-white border border-tnn-ink/15 text-tnn-ink font-bold text-sm tracking-wide px-6 py-3.5 rounded-full hover:border-tnn-purple hover:text-tnn-purple transition-colors duration-200"
              >
                JUST CHECKING
              </button>
              <button
                onClick={close}
                data-testid="masterclass-modal-no"
                className="w-full text-tnn-muted font-semibold text-sm px-6 py-2.5 hover:text-tnn-ink transition-colors duration-200"
              >
                NO, NOT NOW
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
